import React from "react";
import { useTranslation } from "react-i18next";
import { IoMdInformationCircleOutline } from "react-icons/io";
import {
  CatalogImageOwner,
  useFloorplannerProductById,
} from "../../../../features/floorplan3d/catalog/catalogQueries";
import { useEffectiveFloorplannerSymbol3DDefaults } from "../../../../features/floorplan3d/symbols/defaults/floorplannerSymbol3DDefaults";
import {
  getPrimitiveSymbol3DDefaults,
  getPrimitiveSymbol3DModelKey,
  getPrimitiveSymbol3DModelKeyForSymbol,
  isPrimitiveTable3DModelKey,
} from "../../../../features/floorplan3d/symbols/registry/primitiveSymbol3DConfig";
import {
  assetBaseElevationFromEffectiveFloor,
  computeAssetEffectiveFloorDistance,
} from "../../../../features/floorplan3d/symbols/assets/symbol3DFloorDistance";
import {
  getSymbol3DAssetFitMode,
  Symbol3DAssetFitMode,
} from "../../../../features/floorplan3d/symbols/assets/symbol3DAssetTransform";
import {
  boxBaseElevationFromEffectiveFloor,
  computeBoxEffectiveFloorDistance,
} from "../../../../features/floorplan3d/symbols/primitives/shapes/shapePrimitiveGeometry";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import { SvgType } from "../../../../types/floorplannerTypes";
import { ProductLibraryDetailModal } from "../../productLibrary/ProductLibraryDetailModal";
import DimensionField from "../components/DimensionField";
import LinkedDimensionsGroup from "../components/LinkedDimensionsGroup";
import ObjectRotationField from "../components/ObjectRotationField";
import RotationField from "../components/RotationField";
import MetricsSection from "../components/MetricsSection";
import View3DControlsGrid from "../components/View3DControlsGrid";
import View3DSection from "../components/View3DSection";
import UnitHeader from "../components/UnitHeader";
import type { PropertyPanelController } from "../types";
import {
  areNumbersEqual,
  degreesToRadians,
  getCommonDegreesValue,
  getCommonMillimeterValue,
  getCommonValue,
  panelGridFieldClassName,
  panelObjectTitleRowClassName,
  radiansToRoundedDegrees,
  toDisplayLength,
  toWorldFromDisplay,
} from "../propertyPanelUtils";
import { isSymbolAspectRatioLocked } from "../../../../utils/symbolAspectRatio";

type SvgSectionProps = Pick<PropertyPanelController, "updateGlobalState"> & {
  selectedSvgs: SvgType[];
  displayedSvgs: SvgType[];
  is3DView: boolean;
};

export const supportsPrimitive3DControls = (symbols: SvgType[]) =>
  symbols.length > 0 &&
  symbols.every((symbol) => Boolean(getSymbolPrimitive3DModelKey(symbol)));

export const supportsSvg3DControls = (symbols: SvgType[]) =>
  symbols.length > 0 &&
  symbols.every((symbol) => {
    if (getSymbolPrimitive3DModelKey(symbol)) {
      return true;
    }
    if (
      symbol.threeD?.userModel?.status === "ready" &&
      symbol.threeD.userModel.url
    )
      return true;
    if (
      symbol.threeD?.product?.asset?.status === "ready" &&
      symbol.threeD.product.asset.glbUrl
    )
      return true;
    return false;
  });

const getSymbolPrimitive3DModelKey = (symbol: SvgType) =>
  getPrimitiveSymbol3DModelKey(symbol.threeD?.product?.proxyModelKey) ??
  getPrimitiveSymbol3DModelKeyForSymbol(symbol);

const getSymbol3DHeight = (symbol: SvgType) => {
  const key = getSymbolPrimitive3DModelKey(symbol);
  return (
    symbol.threeD?.height ??
    symbol.threeD?.dimensions?.height ??
    symbol.threeD?.userModel?.dimensions?.height ??
    symbol.threeD?.product?.dimensions?.height ??
    getPrimitiveSymbol3DDefaults(key)?.height ??
    0
  );
};

const getSymbol3DBaseElevation = (symbol: SvgType) => {
  const key = getSymbolPrimitive3DModelKey(symbol);
  return (
    symbol.threeD?.baseElevation ??
    getPrimitiveSymbol3DDefaults(key)?.baseElevation ??
    0
  );
};
const getSymbol3DPitchDegrees = (symbol: SvgType) =>
  radiansToRoundedDegrees(symbol.threeD?.rotation3D?.pitch ?? 0);
const getSymbol3DRollDegrees = (symbol: SvgType) =>
  radiansToRoundedDegrees(symbol.threeD?.rotation3D?.roll ?? 0);
const getSymbol3DPlanRotationDegrees = (symbol: SvgType) =>
  radiansToRoundedDegrees(symbol.rotation ?? 0);

const hasUploadedModel3D = (symbol: SvgType) =>
  symbol.threeD?.userModel?.status === "ready" && Boolean(symbol.threeD.userModel.url);

const hasProductModel3D = (symbol: SvgType) =>
  symbol.threeD?.product?.asset?.status === "ready" &&
  Boolean(symbol.threeD.product.asset.glbUrl);

const usesModelFootprint = (symbol: SvgType) =>
  symbol.libraryKey === "USER_MODEL_3D" ||
  hasUploadedModel3D(symbol) ||
  hasProductModel3D(symbol);

/** Footprint width: svgLength for asset-backed 3D models, svgHeight for SVG symbols. */
const getSvgFootprintWidth = (symbol: SvgType) =>
  usesModelFootprint(symbol) ? symbol.svgLength : symbol.svgHeight;

/** Footprint length/depth: svgHeight for asset-backed 3D models, svgLength for SVG symbols. */
const getSvgFootprintLength = (symbol: SvgType) =>
  usesModelFootprint(symbol) ? symbol.svgHeight : symbol.svgLength;

const getSvgAssetBounds = (symbol: SvgType) =>
  symbol.threeD?.userModel?.bounds ?? symbol.threeD?.product?.asset?.bounds;

/**
 * Matches SymbolAsset3DModel sizing behavior:
 * User models and products always stretch to declared dimensions so 3D matches
 * the 2D footprint. Standard assets retain source geometry proportions.
 */
const getSvgAssetFitMode = (symbol: SvgType): Symbol3DAssetFitMode =>
  getSymbol3DAssetFitMode(
    hasUploadedModel3D(symbol)
      ? "userModel"
      : hasProductModel3D(symbol)
        ? "product"
        : "standard",
    isSymbolAspectRatioLocked(symbol.lockAspectRatio),
  );

/**
 * Effective "distance from floor" for an SVG/3D-model symbol, accounting for
 * pitch/roll/yaw rotation. Uses source asset bounds when available so this
 * matches runtime 3D transform behavior exactly; falls back to box approximation.
 */
const getSvgSymbol3DEffectiveFloorDistance = (symbol: SvgType): number => {
  const baseElevation = getSymbol3DBaseElevation(symbol);
  const width = getSvgFootprintWidth(symbol) ?? 0;
  const depth = getSvgFootprintLength(symbol) ?? 0;
  const height = getSymbol3DHeight(symbol);
  const bounds = getSvgAssetBounds(symbol);
  if (bounds) {
    return computeAssetEffectiveFloorDistance(
      baseElevation,
      symbol.threeD?.rotation3D,
      bounds,
      width,
      depth,
      height,
      getSvgAssetFitMode(symbol),
    );
  }
  return computeBoxEffectiveFloorDistance(
    baseElevation,
    symbol.threeD?.rotation3D,
    width / 2,
    depth / 2,
    height,
  );
};

const getSvgAspectRatio = (symbol: SvgType) =>
  symbol.ratio ||
  (getSvgFootprintWidth(symbol) || 1) / (getSvgFootprintLength(symbol) || 1);

const getHeightToWidthRatio = (symbol: SvgType) =>
  (getSymbol3DHeight(symbol) || 1) / (getSvgFootprintWidth(symbol) || 1);

const getHeightToLengthRatio = (symbol: SvgType) =>
  (getSymbol3DHeight(symbol) || 1) / (getSvgFootprintLength(symbol) || 1);

/** Prefer explicit store overrides before admin-default merged metadata. */
const getDisplaySymbol3DHeight = (storeSymbol: SvgType, effectiveSymbol: SvgType) =>
  storeSymbol.threeD?.height ??
  storeSymbol.threeD?.dimensions?.height ??
  getSymbol3DHeight(effectiveSymbol);

type SvgDimensionsMm = {
  width: number;
  depth: number;
  height: number;
};

const getRestoredProductDimensionsMm = (
  symbol: SvgType,
): SvgDimensionsMm | undefined => {
  const source = symbol.threeD?.product?.asset?.dimensions;
  const sourceWidth = source?.width;
  const sourceDepth = source?.depth;
  const sourceHeight = source?.height;
  const currentWidth = getSvgFootprintWidth(symbol);
  if (
    !sourceWidth ||
    !sourceDepth ||
    !sourceHeight ||
    !currentWidth ||
    ![sourceWidth, sourceDepth, sourceHeight, currentWidth].every(
      (dimension) => Number.isFinite(dimension) && dimension > 0,
    )
  ) {
    return undefined;
  }

  const scale = currentWidth / sourceWidth;
  return {
    width: toDisplayLength(currentWidth),
    depth: toDisplayLength(sourceDepth * scale),
    height: toDisplayLength(sourceHeight * scale),
  };
};

const LinkedProductTitle: React.FC<{
  productId: string;
  productName: string;
}> = ({ productId, productName }) => {
  const [detailsRequested, setDetailsRequested] = React.useState(false);
  const { data, loading, error, refetch } = useFloorplannerProductById(
    productId,
    !detailsRequested,
  );
  const product = data?.productById as CatalogImageOwner | undefined;

  React.useEffect(() => setDetailsRequested(false), [productId]);

  return (
    <>
      <div className="min-w-0">
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={detailsRequested && Boolean(product)}
          aria-label={`View product details for ${productName}`}
          aria-busy={loading}
          title={`View product details for ${productName}`}
          onClick={() => setDetailsRequested(true)}
          className="group flex max-w-full items-start gap-1.5 rounded-sm text-left text-gray-900 hover:underline hover:underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
        >
          <span className="min-w-0 break-words">{productName}</span>
          <IoMdInformationCircleOutline
            aria-hidden="true"
            size={17}
            className={`mt-0.5 shrink-0 text-gray-500 group-hover:text-gray-900 ${
              loading ? "animate-pulse" : ""
            }`}
          />
        </button>
        {detailsRequested && loading && (
          <span className="sr-only" role="status">
            Loading product details
          </span>
        )}
        {detailsRequested && error && (
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-1 block text-left text-[11px] leading-4 text-red-700 underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-700"
          >
            Could not load product details. Try again.
          </button>
        )}
      </div>
      {detailsRequested && product && (
        <ProductLibraryDetailModal
          product={product}
          onClose={() => setDetailsRequested(false)}
        />
      )}
    </>
  );
};

const SvgSection: React.FC<SvgSectionProps> = ({
  selectedSvgs,
  displayedSvgs,
  updateGlobalState,
  is3DView,
}) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");
  const effectiveSelectedSvgs = useEffectiveFloorplannerSymbol3DDefaults(
    selectedSvgs,
  );
  const getEffectiveSvg = (svg: SvgType) =>
    effectiveSelectedSvgs.find((candidate) => candidate.id === svg.id) ?? svg;

  return (
  <>
    {displayedSvgs.map((svg) => {
      const effectiveSvg = getEffectiveSvg(svg);
      const displayHeight = getDisplaySymbol3DHeight(svg, effectiveSvg);
      const supports3DControls = supportsSvg3DControls(effectiveSelectedSvgs);
      const show3DControls = is3DView && supports3DControls;
      const usesAssetFootprint = usesModelFootprint(effectiveSvg);
      const isPrimitiveTable =
        !usesAssetFootprint &&
        isPrimitiveTable3DModelKey(
          getSymbolPrimitive3DModelKey(effectiveSvg),
        );
      const lengthLabel = usesAssetFootprint ? t("propertyPanel.depth") : t("propertyPanel.length");
      const aspectLocked = isSymbolAspectRatioLocked(svg.lockAspectRatio);
      // Link height with the footprint under aspect-ratio lock whenever the
      // height is meaningful, regardless of whether the editor is in 2D or 3D.
      const coupleHeightWithFootprint =
        !isPrimitiveTable && (usesAssetFootprint || supports3DControls);
      const preserveEffectiveFloorDistance = (
        getNextDimensionsMm: (symbol: SvgType) => SvgDimensionsMm,
        getNextFitMode: (symbol: SvgType) => Symbol3DAssetFitMode =
          getSvgAssetFitMode,
      ) => {
        if (!supports3DControls) return;

        selectedSvgs.forEach((selectedSvg) => {
          const effective = getEffectiveSvg(selectedSvg);
          const currentEffectiveFloor =
            getSvgSymbol3DEffectiveFloorDistance(effective);
          const nextDimensions = getNextDimensionsMm(effective);
          const nextWidth = toWorldFromDisplay(nextDimensions.width);
          const nextDepth = toWorldFromDisplay(nextDimensions.depth);
          const nextHeight = toWorldFromDisplay(nextDimensions.height);
          const bounds = getSvgAssetBounds(effective);
          const rawBaseElev = bounds
            ? assetBaseElevationFromEffectiveFloor(
                currentEffectiveFloor,
                effective.threeD?.rotation3D,
                bounds,
                nextWidth,
                nextDepth,
                nextHeight,
                getNextFitMode(effective),
              )
            : boxBaseElevationFromEffectiveFloor(
                currentEffectiveFloor,
                effective.threeD?.rotation3D,
                nextWidth / 2,
                nextDepth / 2,
                nextHeight,
              );

          updateGlobalState(
            [selectedSvg],
            "symbol3DBaseElevation",
            toDisplayLength(rawBaseElev),
          );
        });
      };
      const handleHeightChange = (nextValue: string) => {
        const value = parseFloat(nextValue);
        if (!Number.isFinite(value)) return;
        updateGlobalState(selectedSvgs, "symbol3DHeight", value);
        if (aspectLocked && !isPrimitiveTable) {
          const widthProperty = usesAssetFootprint
            ? "svgLength"
            : "svgHeight";
          const lengthProperty = usesAssetFootprint
            ? "svgHeight"
            : "svgLength";
          const currentHeight = displayHeight;
          const currentWidth = getSvgFootprintWidth(effectiveSvg) || 0;
          const currentDepth = getSvgFootprintLength(effectiveSvg) || 0;
          if (currentHeight > 0) {
            const scale = toWorldFromDisplay(value) / currentHeight;
            const nextWidth = toDisplayLength(currentWidth * scale);
            const nextDepth = toDisplayLength(currentDepth * scale);
            updateGlobalState(selectedSvgs, widthProperty, nextWidth);
            updateGlobalState(selectedSvgs, lengthProperty, nextDepth);
            preserveEffectiveFloorDistance(() => ({
              width: nextWidth,
              depth: nextDepth,
              height: value,
            }));
            return;
          }
        }
        preserveEffectiveFloorDistance((effective) => ({
          width: toDisplayLength(
            getSvgFootprintWidth(effective) || 0,
          ),
          depth: toDisplayLength(
            getSvgFootprintLength(effective) || 0,
          ),
          height: value,
        }));
      };

      return (
        <div key={`svg-${svg?.id}`}>
          <div className="border-b border-gray-300" />
          <div className="text-sm font-normal">
            <div className={`${panelObjectTitleRowClassName} items-start`}>
              <UnitHeader
                title={
                  selectedSvgs.length > 1
                    ? t("propertyPanel.multipleSvgSymbols", { count: selectedSvgs.length })
                    : effectiveSvg.threeD?.product?.productId
                      ? (
                          <LinkedProductTitle
                            productId={
                              effectiveSvg.threeD.product.productId
                            }
                            productName={
                              effectiveSvg.threeD.product.name ??
                              svg.name ??
                              t("propertyPanel.catalogProduct")
                            }
                          />
                        )
                      : (svg.name ?? t("propertyPanel.svgSymbol"))
                }
              />
            </div>
            <MetricsSection>
            <LinkedDimensionsGroup
              locked={aspectLocked}
              onToggleLock={() => {
                const newLockState = !aspectLocked;
                const restoredDimensionsById = new Map<
                  string,
                  SvgDimensionsMm
                >();
                selectedSvgs.forEach((selectedSvg) => {
                  const effectiveSelectedSvg = getEffectiveSvg(selectedSvg);
                  floorplannerStore.updateSymbolProperty(
                    selectedSvg.id,
                    "lockAspectRatio",
                    newLockState,
                  );
                  if (newLockState) {
                    const restoredDimensions =
                      getRestoredProductDimensionsMm(effectiveSelectedSvg);
                    if (restoredDimensions) {
                      restoredDimensionsById.set(
                        selectedSvg.id,
                        restoredDimensions,
                      );
                      const lengthProperty = usesModelFootprint(
                        effectiveSelectedSvg,
                      )
                        ? "svgHeight"
                        : "svgLength";
                      updateGlobalState(
                        [selectedSvg],
                        lengthProperty,
                        restoredDimensions.depth,
                      );
                      updateGlobalState(
                        [selectedSvg],
                        "symbol3DHeight",
                        restoredDimensions.height,
                      );
                    }
                    const ratio = restoredDimensions
                      ? restoredDimensions.width / restoredDimensions.depth
                      : (getSvgFootprintWidth(effectiveSelectedSvg) || 1) /
                        (getSvgFootprintLength(effectiveSelectedSvg) || 1);
                    floorplannerStore.updateSymbolProperty(
                      selectedSvg.id,
                      "ratio",
                      ratio,
                    );
                  }
                });
                if (newLockState && restoredDimensionsById.size > 0) {
                  preserveEffectiveFloorDistance(
                    (effective) =>
                      restoredDimensionsById.get(effective.id) ?? {
                        width: toDisplayLength(
                          getSvgFootprintWidth(effective) || 0,
                        ),
                        depth: toDisplayLength(
                          getSvgFootprintLength(effective) || 0,
                        ),
                        height: toDisplayLength(
                          getSymbol3DHeight(effective),
                        ),
                      },
                    (effective) =>
                      getSymbol3DAssetFitMode(
                        hasUploadedModel3D(effective)
                          ? "userModel"
                          : hasProductModel3D(effective)
                            ? "product"
                            : "standard",
                        true,
                      ),
                  );
                }
              }}
              widthField={
                <DimensionField
                  label={t("propertyPanel.width")}
                  fieldClassName={panelGridFieldClassName}
                  value={
                    selectedSvgs.length > 1
                      ? (getCommonMillimeterValue(
                          effectiveSelectedSvgs,
                          getSvgFootprintWidth,
                        ) ?? "")
                      : toDisplayLength(
                          getSvgFootprintWidth(effectiveSvg) || 0,
                        )
                  }
                  placeholder={
                    selectedSvgs.length > 1 ? mixed : undefined
                  }
                  onChange={(nextValue) => {
                    const value = parseFloat(nextValue);
                    if (!Number.isFinite(value)) return;
                    const widthProperty = usesAssetFootprint
                      ? "svgLength"
                      : "svgHeight";
                    const lengthProperty = usesAssetFootprint
                      ? "svgHeight"
                      : "svgLength";
                    updateGlobalState(selectedSvgs, widthProperty, value);
                    if (aspectLocked) {
                      const ratio = getSvgAspectRatio(effectiveSvg);
                      const nextDepth = value / ratio;
                      const nextHeight = coupleHeightWithFootprint
                        ? value * getHeightToWidthRatio(effectiveSvg)
                        : toDisplayLength(getSymbol3DHeight(effectiveSvg));
                      updateGlobalState(
                        selectedSvgs,
                        lengthProperty,
                        nextDepth,
                      );
                      if (coupleHeightWithFootprint) {
                        updateGlobalState(
                          selectedSvgs,
                          "symbol3DHeight",
                          nextHeight,
                        );
                      }
                      preserveEffectiveFloorDistance(() => ({
                        width: value,
                        depth: nextDepth,
                        height: nextHeight,
                      }));
                    } else {
                      preserveEffectiveFloorDistance((effective) => ({
                        width: value,
                        depth: toDisplayLength(
                          getSvgFootprintLength(effective) || 0,
                        ),
                        height: toDisplayLength(
                          getSymbol3DHeight(effective),
                        ),
                      }));
                    }
                  }}
                />
              }
              lengthField={
                <DimensionField
                  label={lengthLabel}
                  fieldClassName={panelGridFieldClassName}
                  value={
                    selectedSvgs.length > 1
                      ? (getCommonMillimeterValue(
                          effectiveSelectedSvgs,
                          getSvgFootprintLength,
                        ) ?? "")
                      : toDisplayLength(
                          getSvgFootprintLength(effectiveSvg) || 0,
                        )
                  }
                  placeholder={
                    selectedSvgs.length > 1 ? mixed : undefined
                  }
                  onChange={(nextValue) => {
                    const value = parseFloat(nextValue);
                    if (!Number.isFinite(value)) return;
                    const widthProperty = usesAssetFootprint
                      ? "svgLength"
                      : "svgHeight";
                    const lengthProperty = usesAssetFootprint
                      ? "svgHeight"
                      : "svgLength";
                    updateGlobalState(selectedSvgs, lengthProperty, value);
                    if (aspectLocked) {
                      const ratio = getSvgAspectRatio(effectiveSvg);
                      const nextWidth = value * ratio;
                      const nextHeight = coupleHeightWithFootprint
                        ? value * getHeightToLengthRatio(effectiveSvg)
                        : toDisplayLength(getSymbol3DHeight(effectiveSvg));
                      updateGlobalState(
                        selectedSvgs,
                        widthProperty,
                        nextWidth,
                      );
                      if (coupleHeightWithFootprint) {
                        updateGlobalState(
                          selectedSvgs,
                          "symbol3DHeight",
                          nextHeight,
                        );
                      }
                      preserveEffectiveFloorDistance(() => ({
                        width: nextWidth,
                        depth: value,
                        height: nextHeight,
                      }));
                    } else {
                      preserveEffectiveFloorDistance((effective) => ({
                        width: toDisplayLength(
                          getSvgFootprintWidth(effective) || 0,
                        ),
                        depth: value,
                        height: toDisplayLength(
                          getSymbol3DHeight(effective),
                        ),
                      }));
                    }
                  }}
                />
              }
              heightField={
                supports3DControls ? (
                  <DimensionField
                    label={t("propertyPanel.height")}
                    fieldClassName={panelGridFieldClassName}
                    value={
                      selectedSvgs.length > 1
                        ? (getCommonMillimeterValue(
                            effectiveSelectedSvgs,
                            getSymbol3DHeight,
                          ) ?? "")
                        : toDisplayLength(displayHeight)
                    }
                    placeholder={
                      selectedSvgs.length > 1
                        ? mixed
                        : undefined
                    }
                    onChange={handleHeightChange}
                  />
                ) : undefined
              }
              trailingField={
                !is3DView || !show3DControls ? (
                  <RotationField
                    value={
                      selectedSvgs.length > 1
                        ? (getCommonDegreesValue(selectedSvgs) ?? "")
                        : radiansToRoundedDegrees(svg?.rotation || 0)
                    }
                    placeholder={
                      selectedSvgs.length > 1
                        ? mixed
                        : undefined
                    }
                    onChange={(degrees) =>
                      updateGlobalState(
                        selectedSvgs,
                        "rotation",
                        degreesToRadians(degrees),
                      )
                    }
                  />
                ) : undefined
              }
            />
            </MetricsSection>
            {show3DControls && (
              <View3DSection>
                <View3DControlsGrid>
                  <DimensionField
                    label={t("propertyPanel.distanceFromFloor")}
                    fieldClassName={panelGridFieldClassName}
                    value={
                      selectedSvgs.length > 1
                        ? (getCommonMillimeterValue(
                            effectiveSelectedSvgs,
                            getSvgSymbol3DEffectiveFloorDistance,
                          ) ?? "")
                        : toDisplayLength(
                            getSvgSymbol3DEffectiveFloorDistance(effectiveSvg),
                          )
                    }
                    placeholder={
                      selectedSvgs.length > 1
                        ? mixed
                        : undefined
                    }
                    onChange={(nextValue) => {
                      const parsedValue = parseFloat(nextValue);
                      if (!Number.isFinite(parsedValue)) return;
                      const effectiveWorld =
                        toWorldFromDisplay(parsedValue);
                      selectedSvgs.forEach((s) => {
                        const effective = effectiveSelectedSvgs.find(
                          (e) => e.id === s.id,
                        ) ?? s;
                        const width = getSvgFootprintWidth(effective) ?? 0;
                        const depth = getSvgFootprintLength(effective) ?? 0;
                        const height = getSymbol3DHeight(effective);
                        const bounds = getSvgAssetBounds(effective);
                        const rawBaseElev = bounds
                          ? assetBaseElevationFromEffectiveFloor(
                              effectiveWorld,
                              effective.threeD?.rotation3D,
                              bounds,
                              width,
                              depth,
                              height,
                              getSvgAssetFitMode(effective),
                            )
                          : boxBaseElevationFromEffectiveFloor(
                              effectiveWorld,
                              effective.threeD?.rotation3D,
                              width / 2,
                              depth / 2,
                              height,
                            );
                        updateGlobalState(
                          [s],
                          "symbol3DBaseElevation",
                          toDisplayLength(rawBaseElev),
                        );
                      });
                    }}
                  />
                  <ObjectRotationField
                    label={t("propertyPanel.rotateObject")}
                    value={
                      selectedSvgs.length > 1
                        ? (getCommonValue(
                            effectiveSelectedSvgs,
                            getSymbol3DPlanRotationDegrees,
                            areNumbersEqual,
                          ) ?? "")
                        : getSymbol3DPlanRotationDegrees(effectiveSvg)
                    }
                    placeholder={
                      selectedSvgs.length > 1
                        ? mixed
                        : undefined
                    }
                    onChange={(nextValue) => {
                      const degrees = parseFloat(nextValue);
                      updateGlobalState(
                        selectedSvgs,
                        "rotation",
                        degreesToRadians(Number.isFinite(degrees) ? degrees : 0),
                      );
                    }}
                  />
                  <ObjectRotationField
                    label={t("propertyPanel.turnObject")}
                    value={
                      selectedSvgs.length > 1
                        ? (getCommonValue(
                            effectiveSelectedSvgs,
                            getSymbol3DPitchDegrees,
                            areNumbersEqual,
                          ) ?? "")
                        : getSymbol3DPitchDegrees(effectiveSvg)
                    }
                    placeholder={
                      selectedSvgs.length > 1
                        ? mixed
                        : undefined
                    }
                    onChange={(nextValue) =>
                      updateGlobalState(
                        selectedSvgs,
                        "shape3DPitch",
                        nextValue,
                      )
                    }
                  />
                  <ObjectRotationField
                    label={t("propertyPanel.tiltObject")}
                    value={
                      selectedSvgs.length > 1
                        ? (getCommonValue(
                            effectiveSelectedSvgs,
                            getSymbol3DRollDegrees,
                            areNumbersEqual,
                          ) ?? "")
                        : getSymbol3DRollDegrees(effectiveSvg)
                    }
                    placeholder={
                      selectedSvgs.length > 1
                        ? mixed
                        : undefined
                    }
                    onChange={(nextValue) =>
                      updateGlobalState(
                        selectedSvgs,
                        "shape3DRoll",
                        nextValue,
                      )
                    }
                  />
                </View3DControlsGrid>
              </View3DSection>
            )}
          </div>
          <div className="border-b border-gray-300" />
        </div>
      );
    })}
  </>
  );
};

export default SvgSection;
